import React from 'react';
import axios from 'axios';

import Loading from './Loading';
import { useFlipOIDC } from './FlipOIDC';

export interface Image {
  id: string;
  link: string;
  file_name: string;
}

export interface DesignTokenColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface DesignToken {
  id: string;
  color: DesignTokenColor;
}

export interface Theme {
  favicon: Image;
  company_icon: Image;
  company_logo: Image;
  design_tokens: DesignToken[];
  active: boolean;
}

export interface Themes {
  dark: Theme;
  light: Theme;
}

interface FlipThemesContextProps {
  logo: string;
}

function rgba(color: DesignTokenColor) {
  return (`rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`);
}

const FlipThemesContext = React.createContext<FlipThemesContextProps | undefined>(undefined);

export function useFlipThemes() {
  const context = React.useContext(FlipThemesContext);
  if (!context) throw new Error('useFlipThemes must be used inside the FlipThemes provider.');
  return context;
}

export default function FlipThemes({ children }: React.PropsWithChildren) {
  const { token } = useFlipOIDC();

  const [loading, setLoading] = React.useState(true);
  const [themes, setThemes] = React.useState<Themes | undefined>();
  const [logo, setLogo] = React.useState('');
  const css = React.useRef<HTMLStyleElement>();

  React.useEffect(() => {
    async function load() {
      const { data } = await axios.get<Themes>('/api/v2/themes', { headers: { Authorization: `Bearer ${token}` } });
      setThemes(data);
      setLoading(false);
    }

    if (!themes) load();
  }, [token, themes]);

  React.useEffect(() => {
    if (themes) {
      setLogo(themes.light.company_logo.link);

      if (!css.current) {
        css.current = document.createElement('style');
        document.head.append(css.current);
      }

      const styles = [
        ':root {',
        ...themes.light.design_tokens.map((d) => (`  --${d.id}: ${rgba(d.color)};`)),
        '}',
      ].join('\n');

      css.current.innerHTML = styles;
    }
  }, [themes]);

  const value = React.useMemo(() => ({
    logo,
  }), [logo]);

  return (
    <FlipThemesContext.Provider value={value}>
      <Loading loading={loading}>
        {children}
      </Loading>
    </FlipThemesContext.Provider>
  );
}
